import {Routes, Route} from 'react-router-dom';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {IntlProvider} from 'react-intl';
// TODO: get rid of
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {createMuiTheme} from '@material-ui/core/styles';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import DOMPurify from 'isomorphic-dompurify';
import {useInitializeSurvicateScript} from '@hooks/Survicate/useInitializeSurvicateScript';
import {DEFAULT_LOCALE} from '@constants/main';
import {useBranding} from '@theme';
import Layout from '@components/common/Layout/Layout';
import {LayoutV2} from '@components/common/LayoutV2/LayoutV2';
import ErrorPage from '@components/common/ErrorPage/ErrorPage';
import {BroadcastStation} from '@components/BroadcastStation';
import {
    selectTranslations, selectLocale, selectConfig, selectTheme, selectSurvicateWorkspaceKey
} from '@components/appSlice';
import {
    selectAccount,
    selectFeatures, selectHasUserPermissionOnRoot, selectPermissions, selectUser,
    selectGetIsDelegation, selectAuthUserRole
} from '@components/Login/userSlice';
import {ProtectedRoute} from '@components/ProtectedRoute';
import {getAvailableMainRoutes, getAvailableSigningRoutes} from '@utils/routes';
import {capitalizeFirstLetter} from '@utils/helpers';
import {useTokenRefresh} from '@hooks/useTokenRefresh';
import useSenderPrefetch from '@hooks/useSenderPrefetch';
import useEmailAliasesLoader from '@hooks/useEmailAliasesLoader';
import {VS_FEATURES} from '@constants';
import VSFeature, {Disabled, Enabled} from '@components/shared/VSFeature/VSFeature';
import TwoFactorEnforcementModal from '@components/shared/TwoFactorEnforcementModal/TwoFactorEnforcementModal';
import '@components/shared/app.less';
import '@components/shared/notifications.less';
import '@ValidSignBV/vs-ui/dist/style.css'; // TODO: Figure it out why styles are not embaded into components

DOMPurify.setConfig({
    USE_PROFILES: {html: true},
    IN_PLACE: true,
    ADD_ATTR: ['target']
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const target = node.getAttribute('target');

    if (typeof (target) === 'string' && target.toLowerCase() === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
    } else {
        node.removeAttribute('target');
    }
});

function App() {
    const {isInitializing} = useTokenRefresh();
    const translations = useSelector(selectTranslations);
    const locale = useSelector(selectLocale);
    const user = useSelector(selectUser);
    const userEmail = user?.email || '';
    const account = useSelector(selectAccount);
    const features = useSelector(selectFeatures);
    const permissions = useSelector(selectPermissions);
    const hasUserPermissionOnRoot = useSelector(selectHasUserPermissionOnRoot);
    const survicateWorkspaceKey = useSelector(selectSurvicateWorkspaceKey);
    const theme = useSelector(selectTheme);
    const config = useSelector(selectConfig);
    const isDelegation = useSelector(selectGetIsDelegation);
    const userRole = useSelector(selectAuthUserRole) || '';
    const featuresMap = useSelector(selectFeatures);
    const enabledFeatures = Object.entries(featuresMap || {})
        .filter(([, enabled]) => enabled)
        .map(([key]) => key)
        .join(',');

    const mainRoutes = getAvailableMainRoutes({
        user, features, permissions, hasUserPermissionOnRoot, isDelegation
    });
    const signerRoutes = getAvailableSigningRoutes({
        user, features, permissions, hasUserPermissionOnRoot, isDelegation
    });
    const enabledRoutes = [...mainRoutes, ...signerRoutes];
    const accountProperties = {
        // `account` (legacy session payload) carries id/licenses only; company/address live on `user`.
        country: user?.address?.country || '',
        company: user?.company || '',
        accountId: account?.id || '',
        subscriptionPlan: ''
    };

    useInitializeSurvicateScript({
        // `session.user` already is the effective user: during a delegation it holds the account
        // being worked on, while `session.delegationUser` keeps the original login.
        userId: user?.id || '',
        userEmail,
        locale: locale || DEFAULT_LOCALE,
        createdDate: user?.created || '',
        accountProperties,
        isSender: true,
        survicateWorkspaceKey: survicateWorkspaceKey || '',
        environment: config?.VS_ENV || '',
        theme: theme || '',
        userRole,
        features: enabledFeatures
    });

    moment.locale(locale);

    if (locale === 'nl') {
        moment.updateLocale('nl', {
            monthsShort: moment.monthsShort('-MMM-').map(capitalizeFirstLetter),
            months: moment.months().map(capitalizeFirstLetter)
        });
    }

    useBranding();

    useSenderPrefetch({enabled: !!user?.id && !isInitializing});

    useEmailAliasesLoader({isEnabled: !isInitializing});

    if (isInitializing) {
        return null;
    }

    const outerTheme = createMuiTheme({
        typography: {
            fontFamily: 'var(--font-family-body), sans-serif'
        },
        shape: {
            borderRadius: 6
        },
        palette: {
            primary: {
                main: '#2f507b',
                contrastText: '#fff'
            },
            optionalSignatureButton: null,
            signatureButton: null
        },
        props: {
            // TODO: https://github.com/mui/material-ui/issues/30953
            MuiButtonBase: {disableRipple: true},
            MuiTextField: {
                variant: 'outlined'
            },
            MuiFormControl: {
                variant: 'outlined'
            },
            MuiSelect: {
                variant: 'outlined'
            },
            MuiOutlinedInput: {
                padding: '11px'
            }
        },
        overrides: {
            MuiButton: {
                root: {
                    textTransform: 'none',
                    borderRadius: 6
                }
            }
        }
    });

    const routes = (
        <Routes>
            {enabledRoutes.map(({
                component: Component, childrenRoutes = [], isPrivate, end, ...route
            }) => (
                <Route
                    key={`route-${route.path}`}
                    path={route.path}
                    end={end}
                    element={isPrivate ? (
                        <ProtectedRoute>
                            <Component />
                        </ProtectedRoute>
                    ) : (
                        <Component />
                    )}
                >
                    {childrenRoutes?.map(({component: NestedComponent, ...rest}) => (
                        <Route
                            key={`childRoute-${rest.path}`}
                            element={(<NestedComponent />)}
                            {...rest}
                        />
                    ))}
                </Route>
            ))}
            <Route component={ErrorPage} />
        </Routes>
    );

    const renderLayout = () => (
        <VSFeature feature={VS_FEATURES.DESIGN_V2}>
            <Enabled>
                <LayoutV2>
                    {routes}
                </LayoutV2>
            </Enabled>
            <Disabled>
                <Layout>
                    {routes}
                </Layout>
            </Disabled>
        </VSFeature>
    );

    return (
        <IntlProvider locale={locale} messages={translations}>
            <ThemeProvider theme={outerTheme}>
                <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={locale}>
                    <BroadcastStation>
                        {renderLayout()}
                        <TwoFactorEnforcementModal />
                    </BroadcastStation>
                </MuiPickersUtilsProvider>
            </ThemeProvider>
        </IntlProvider>
    );
}

export default App;
